import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { MapPin, Leaf, FileCheck2 } from "lucide-react";
import RevealText from "./RevealText";

/**
 * ThreeStepFlow — three-step request flow.
 *
 * Matches the user-supplied reference: editorial title, three big circular
 * green-tint icon badges, dashed connectors between them, and a short
 * caption block under each. Mobile collapses to a single column with a
 * vertical dashed connector.
 */
const STEPS = [
  { icon: MapPin, titleKey: "home.flow3Step1Title", bodyKey: "home.flow3Step1Body" },
  { icon: Leaf, titleKey: "home.flow3Step2Title", bodyKey: "home.flow3Step2Body" },
  { icon: FileCheck2, titleKey: "home.flow3Step3Title", bodyKey: "home.flow3Step3Body" },
];

function StepIcon({ Icon }) {
  return (
    <div className="relative grid place-items-center">
      {/* Outer soft halo */}
      <span
        aria-hidden
        className="absolute rounded-full"
        style={{
          width: 132,
          height: 132,
          background:
            "radial-gradient(closest-side, rgba(47,122,58,0.16) 0%, rgba(47,122,58,0.06) 60%, rgba(47,122,58,0) 100%)",
        }}
      />
      {/* Inner solid tint disc */}
      <span
        aria-hidden
        className="absolute rounded-full"
        style={{
          width: 96,
          height: 96,
          background: "rgba(47,122,58,0.10)",
        }}
      />
      {/* Icon */}
      <span className="relative grid place-items-center">
        <Icon className="w-10 h-10 text-velcor-green" strokeWidth={1.7} />
      </span>
    </div>
  );
}

export default function ThreeStepFlow() {
  const { t } = useTranslation();

  return (
    <section
      id="how-it-works"
      className="relative max-w-[1180px] mx-auto px-5 py-16 md:py-20"
      data-testid="flow3"
    >
      {/* Title — editorial, matches reference */}
      <div className="text-center max-w-[920px] mx-auto mb-14 md:mb-20">
        <h2
          className="display text-velcor-ink leading-[1.06]"
          style={{ fontSize: "clamp(28px, 4.6vw, 50px)", fontWeight: 600, letterSpacing: "-0.02em" }}
        >
          <RevealText text={t("home.flow3Title")} />{" "}
          <RevealText
            text={t("home.flow3TitleAccent")}
            className="display-accent"
            delay={0.18}
          />
        </h2>
      </div>

      {/* Steps grid */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-start md:items-center gap-y-10 md:gap-y-0 gap-x-0 md:gap-x-4">
        {STEPS.map((s, idx) => (
          <React.Fragment key={s.titleKey}>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: idx * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col items-center text-center cursor-default"
              data-testid={`flow3-step-${idx + 1}`}
            >
              <StepIcon Icon={s.icon} />
              <h3
                className="mt-7 md:mt-10 text-velcor-ink tracking-tight"
                style={{
                  fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, sans-serif",
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                {t(s.titleKey)}
              </h3>
              <p
                className="mt-3 text-slate-500 leading-relaxed max-w-[280px]"
                style={{ fontSize: 14, fontWeight: 400 }}
              >
                {t(s.bodyKey)}
              </p>
            </motion.div>

            {/* Dashed connector — desktop only, between steps */}
            {idx < STEPS.length - 1 && (
              <motion.div
                aria-hidden
                initial={{ scaleX: 0, opacity: 0 }}
                whileInView={{ scaleX: 1, opacity: 1 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: idx * 0.12 + 0.25, ease: "easeOut" }}
                className="hidden md:flex items-center justify-center origin-left"
                style={{ minWidth: 60, marginTop: -90 }}
              >
                <span
                  className="block w-full"
                  style={{
                    height: 0,
                    borderTop: "2px dashed rgba(15,23,42,0.18)",
                    minWidth: 80,
                  }}
                />
              </motion.div>
            )}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}
